import { useState, Fragment } from 'react'
import { ImportItemMetadata } from '../../../types/response'
import { Badge } from '@/components/ui/badge'
import { Button } from 'vnetwork-ui'
import {
    Trash2,
    Package,
    Cpu,
    ChevronDown,
    ChevronRight,
    CornerDownRight,
    Pencil,
} from 'lucide-react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'

interface ImportItemsTableProps {
    items: ImportItemMetadata[]
    onRemove: (index: number) => void
    onEdit?: (index: number) => void
}

const formatPrice = (price?: number): string => {
    if (!price) return '-'
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
    }).format(price)
}

// Generate preview ref following backend legacy pattern: {CATEGORY}.{NAME}.{YYYYMM}
const generateRefPreview = (
    category: string,
    name: string,
    purchaseDate?: string,
    parentRef?: string
): string => {
    const cat = category.toUpperCase()
    // Clean name: uppercase, remove special chars, limit length
    const cleanName = name
        .toUpperCase()
        .replace(/[^A-Z0-9\s]/g, '')
        .trim()
        .slice(0, 20)

    // Get YYYYMM from purchase_date or current date
    const date = purchaseDate ? new Date(purchaseDate) : new Date()
    const yyyymm = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`

    const ref = cleanName ? `${cat}.${cleanName}.${yyyymm}` : `${cat}.${yyyymm}`

    return parentRef ? `${parentRef}_${ref}` : ref
}

// Count total items including children
const countTotalItems = (items: ImportItemMetadata[]): number => {
    return items.reduce((sum, item) => {
        // Count parent items
        let parentCount = 0
        if (item.service_tags && item.service_tags.length > 0) {
            parentCount = item.service_tags.length
        } else {
            parentCount = item.quantity || 1
        }

        // Count children items
        let childCount = 0
        if (item.children && item.children.length > 0) {
            item.children.forEach((child) => {
                if (child.service_tags && child.service_tags.length > 0) {
                    childCount += child.service_tags.length
                } else {
                    childCount += child.quantity || 1
                }
            })
        }

        return sum + parentCount + childCount
    }, 0)
}

const ImportItemsTable = ({
    items,
    onRemove,
    onEdit,
}: ImportItemsTableProps) => {
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

    const toggleExpand = (index: number) => {
        setExpandedItems((prev) => {
            const next = new Set(prev)
            if (next.has(index)) {
                next.delete(index)
            } else {
                next.add(index)
            }
            return next
        })
    }

    if (items.length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground border rounded-lg">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>
                    No items added yet. Use the form above to add items for
                    import.
                </p>
            </div>
        )
    }

    const totalItems = countTotalItems(items)

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                    {items.length} item group(s), {totalItems} total items to
                    create
                </p>
            </div>
            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[40px]"></TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Service Tags / IMEI / SN</TableHead>
                            <TableHead>Condition</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead className="w-[80px]">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((item, index) => (
                            <Fragment key={index}>
                                {/* Parent Row */}
                                <TableRow>
                                    <TableCell className="py-2">
                                        {item.children &&
                                        item.children.length > 0 ? (
                                            <Button
                                                variant="text"
                                                size="small"
                                                type="button"
                                                onClick={() =>
                                                    toggleExpand(index)
                                                }
                                                className="p-1"
                                            >
                                                {expandedItems.has(index) ? (
                                                    <ChevronDown className="w-4 h-4" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4" />
                                                )}
                                            </Button>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Badge
                                                variant={
                                                    item.item_type ===
                                                    'equipment'
                                                        ? 'default'
                                                        : 'secondary'
                                                }
                                            >
                                                {item.item_type ===
                                                'equipment' ? (
                                                    <Package className="w-3 h-3 mr-1" />
                                                ) : (
                                                    <Cpu className="w-3 h-3 mr-1" />
                                                )}
                                                {item.item_type}
                                            </Badge>
                                            {item.children &&
                                                item.children.length > 0 && (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-xs"
                                                    >
                                                        +{item.children.length}
                                                    </Badge>
                                                )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="capitalize">
                                        {item.category}
                                    </TableCell>
                                    <TableCell>{item.name || '-'}</TableCell>
                                    <TableCell>
                                        {item.service_tags &&
                                        item.service_tags.length > 0 ? (
                                            <div className="flex flex-wrap gap-1 max-w-[300px]">
                                                {item.service_tags
                                                    .slice(0, 3)
                                                    .map((tag, i) => (
                                                        <Badge
                                                            key={i}
                                                            variant="outline"
                                                            className="text-xs"
                                                        >
                                                            {tag}
                                                        </Badge>
                                                    ))}
                                                {item.service_tags.length >
                                                    3 && (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-xs"
                                                    >
                                                        +
                                                        {item.service_tags
                                                            .length - 3}{' '}
                                                        more
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex flex-wrap gap-1 max-w-[300px]">
                                                <Badge
                                                    variant="secondary"
                                                    className="text-xs font-mono"
                                                >
                                                    {generateRefPreview(
                                                        item.category,
                                                        item.name,
                                                        item.purchase_date
                                                    )}
                                                </Badge>
                                                {(item.quantity || 1) > 1 && (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-xs"
                                                    >
                                                        ×{item.quantity}
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="capitalize">
                                        {item.condition}
                                    </TableCell>
                                    <TableCell>
                                        {formatPrice(item.purchase_price)}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {onEdit && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    type="button"
                                                    onClick={() =>
                                                        onEdit(index)
                                                    }
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="text"
                                                color="error"
                                                size="small"
                                                type="button"
                                                onClick={() => onRemove(index)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>

                                {/* Child Rows - Expanded */}
                                {expandedItems.has(index) &&
                                    item.children?.map((child, childIdx) => (
                                        <TableRow
                                            key={`${index}-${childIdx}`}
                                            className="bg-muted/30"
                                        >
                                            <TableCell></TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 pl-2">
                                                    <CornerDownRight className="w-4 h-4 text-muted-foreground" />
                                                    <Badge variant="secondary">
                                                        <Cpu className="w-3 h-3 mr-1" />
                                                        {child.item_type}
                                                    </Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell className="capitalize">
                                                {child.category}
                                            </TableCell>
                                            <TableCell>
                                                {child.name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {child.service_tags &&
                                                child.service_tags.length >
                                                    0 ? (
                                                    <div className="flex flex-wrap gap-1 max-w-[250px]">
                                                        {child.service_tags
                                                            .slice(0, 2)
                                                            .map((tag, i) => (
                                                                <Badge
                                                                    key={i}
                                                                    variant="outline"
                                                                    className="text-xs"
                                                                >
                                                                    {tag}
                                                                </Badge>
                                                            ))}
                                                        {child.service_tags
                                                            .length > 2 && (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-xs"
                                                            >
                                                                +
                                                                {child
                                                                    .service_tags
                                                                    .length -
                                                                    2}{' '}
                                                                more
                                                            </Badge>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1 max-w-[250px]">
                                                        <Badge
                                                            variant="secondary"
                                                            className="text-xs font-mono"
                                                        >
                                                            {generateRefPreview(
                                                                child.category,
                                                                child.name,
                                                                child.purchase_date,
                                                                item
                                                                    .service_tags?.[0] ||
                                                                    generateRefPreview(
                                                                        item.category,
                                                                        item.name,
                                                                        item.purchase_date
                                                                    )
                                                            )}
                                                        </Badge>
                                                        {(child.quantity || 1) >
                                                            1 && (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-xs"
                                                            >
                                                                ×
                                                                {child.quantity}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="capitalize">
                                                {child.condition}
                                            </TableCell>
                                            <TableCell>-</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    ))}
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}

export default ImportItemsTable
